import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { paths } from "@clevis/shared-types";
import { Button, Card, CardTitle, ConfirmButton, Select } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { useEntitlements } from "../../hooks/useEntitlements";
import { QbLearnCard } from "../../components/QbLearnCard";
import { QbBillSettingsCard } from "../../components/QbBillSettingsCard";
import { useAccess } from "../../hooks/useAccess";

interface QbConfig {
  qbRealmId: string | null;
  qbCompanyName: string | null;
  lastSync: { at: number; ok: boolean; message: string } | null;
}

// Connection page for QuickBooks (Online and Desktop). The sync itself lives
// on each invoice — this page owns connect/disconnect and health.
export function QuickBooksPage() {
  const { hasIntegration } = useEntitlements();
  const can = useAccess();
  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <h1 className="text-lg font-bold">QuickBooks</h1>
      {hasIntegration("quickbooks-online") ? <QboSection /> : null}
      {hasIntegration("quickbooks-desktop") ? <QbdSection /> : null}
      {(hasIntegration("quickbooks-online") || hasIntegration("quickbooks-desktop")) && can("quickbooks.estimates") ? (
        <div id="estimates">
          <EstimateOption />
        </div>
      ) : null}
      <Card>
        <CardTitle>How syncing works</CardTitle>
        <ul className="list-disc space-y-1 pl-5 text-xs text-steel">
          <li>Open an invoice list row and press the QB button to sync it (customer included).</li>
          <li>Recorded payments ride along and link to the QuickBooks invoice.</li>
          <li>A green chip means synced; a red chip shows the exact QuickBooks error and retries on click.</li>
          <li>Desktop pushes run on the Web Connector's next check-in — usually under a minute.</li>
          <li>Expenses go to QuickBooks as vendor bills from the Expenses page, the way the Bills settings above say.</li>
        </ul>
      </Card>
    </div>
  );
}

// Estimates are optional in QuickBooks: some companies only want invoices
// there. Off by default; on shows a QB button on each sent estimate.
function EstimateOption() {
  const { tenantId } = useAuth();
  const [on, setOn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(doc(db, paths.tenant(tenantId)), (snap) => setOn(snap.get("qbSendEstimates") === true));
  }, [tenantId]);
  if (on === null) return null;
  return (
    <Card>
      <CardTitle>Estimates</CardTitle>
      <label className="flex cursor-pointer items-start gap-2 text-sm text-ink">
        <input
          type="checkbox"
          className="mt-1"
          checked={on}
          disabled={busy}
          onChange={async (e) => {
            if (!tenantId) return;
            setBusy(true);
            setError("");
            try {
              await call("setQbEstimateOption", { tenantId, enabled: e.target.checked });
            } catch (err) {
              setError((err as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        />
        <span>
          Send estimates to QuickBooks
          <span className="block text-xs text-steel">
            Sent estimates get a QB button on the Estimates list. They use the same items, class, tax code and terms as
            your invoices. In QuickBooks Online, sending again updates the estimate's status (pending, accepted,
            rejected).
          </span>
        </span>
      </label>
      {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
    </Card>
  );
}

function QboSection() {
  const { tenantId } = useAuth();
  const [cfg, setCfg] = useState<QbConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(doc(db, paths.tenantIntegrationConfig(tenantId, "quickbooks-online")), (snap) =>
      setCfg(
        snap.exists()
          ? {
              qbRealmId: (snap.get("qbRealmId") as string) ?? null,
              qbCompanyName: (snap.get("qbCompanyName") as string) ?? null,
              lastSync: (snap.get("lastSync") as QbConfig["lastSync"]) ?? null,
            }
          : null,
      ),
    );
  }, [tenantId]);

  const connected = !!cfg?.qbRealmId;

  async function connect() {
    if (!tenantId) return;
    setBusy(true);
    setError("");
    try {
      const res = await call<{ url: string }>("getQbConnectUrl", { tenantId }, { mutation: false });
      window.location.href = res.url;
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  async function disconnect() {
    if (!tenantId) return;
    setBusy(true);
    setError("");
    try {
      await call("disconnectQuickBooks", { tenantId }, { mutation: false });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
      <Card>
        <CardTitle>QuickBooks Online</CardTitle>
        {connected ? (
          <>
            <p className="text-sm text-ink">
              Connected to <strong>{cfg?.qbCompanyName ?? "your QuickBooks company"}</strong>
            </p>
            {cfg?.lastSync ? (
              <p className={`mt-1 text-xs ${cfg.lastSync.ok ? "text-success" : "text-danger"}`}>
                {cfg.lastSync.ok ? "✓" : "⚠"} {cfg.lastSync.message} ·{" "}
                {new Date(cfg.lastSync.at).toLocaleString(undefined, {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </p>
            ) : null}
            <ConfirmButton
              className="mt-3"
              confirm="Disconnect? Synced records stay in QuickBooks; new syncs stop."
              confirmLabel="Disconnect"
              onConfirm={() => void disconnect()}
              busy={busy}
            >
              Disconnect
            </ConfirmButton>
            <QbLearnCard target="online" />
            <QboMapping />
            <BillsCard target="online" />
          </>
        ) : (
          <>
            <p className="mb-3 text-sm text-steel">
              Connect your QuickBooks Online company to sync customers, invoices, and payments.
              Every sync shows its result on the invoice — when something fails, you see why and can
              retry. No mystery badges.
            </p>
            <Button onClick={() => void connect()} busy={busy}>
              Connect to QuickBooks
            </Button>
          </>
        )}
        {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
      </Card>
  );
}

interface CacheEntry {
  id: string;
  name: string;
  type?: string;
}

// Per-company mapping: scan their file, then choose exactly how Clevis
// invoices land in it — their default item, class, and whether matching
// line descriptions use their real items.
function QboMapping() {
  const { tenantId } = useAuth();
  const [items, setItems] = useState<CacheEntry[] | null>(null);
  const [classes, setClasses] = useState<CacheEntry[]>([]);
  const [profile, setProfile] = useState({
    itemStrategy: "generic" as "generic" | "matchByName",
    defaultItemId: "",
    classId: "",
  });
  const [busy, setBusy] = useState<"scan" | "save" | null>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(doc(db, paths.tenantQbCache(tenantId, "items")), (snap) =>
        setItems(snap.exists() ? ((snap.get("entries") as CacheEntry[]) ?? []) : null),
      ),
      onSnapshot(doc(db, paths.tenantQbCache(tenantId, "classs")), (snap) =>
        setClasses(snap.exists() ? ((snap.get("entries") as CacheEntry[]) ?? []) : []),
      ),
      onSnapshot(doc(db, paths.tenantIntegrationConfig(tenantId, "quickbooks-online")), (snap) => {
        const p = snap.get("syncProfile") as
          | { itemStrategy?: "generic" | "matchByName"; defaultItemId?: string | null; classId?: string | null }
          | undefined;
        if (p) {
          setProfile({
            itemStrategy: p.itemStrategy ?? "generic",
            defaultItemId: p.defaultItemId ?? "",
            classId: p.classId ?? "",
          });
        }
      }),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId]);

  async function scan() {
    if (!tenantId) return;
    setBusy("scan");
    setMessage("");
    try {
      const res = await call<{ counts: Record<string, number> }>("scanQbCompany", { tenantId }, { mutation: false });
      setMessage(
        `Scanned: ${res.counts.items} items, ${res.counts.accounts} accounts, ${res.counts.classes} classes.`,
      );
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    if (!tenantId) return;
    setBusy("save");
    setMessage("");
    try {
      await call(
        "setQbSyncProfile",
        {
          tenantId,
          profile: {
            itemStrategy: profile.itemStrategy,
            defaultItemId: profile.defaultItemId || null,
            defaultItemName: items?.find((i) => i.id === profile.defaultItemId)?.name ?? null,
            classId: profile.classId || null,
            className: classes.find((c) => c.id === profile.classId)?.name ?? null,
          },
        },
        { mutation: false },
      );
      setMessage("Sync settings saved.");
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mt-4 border-t border-steel/15 pt-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-ink">How invoices land in YOUR QuickBooks</p>
        <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => void scan()} busy={busy === "scan"}>
          {items ? "Re-scan company file" : "Scan company file"}
        </Button>
      </div>
      {items === null ? (
        <p className="mt-1 text-xs text-steel">
          Scan your company file first — Clevis reads your items, accounts and classes so the choices
          below are YOUR choices, not guesses.
        </p>
      ) : (
        <div className="mt-2 space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-0.5 block text-xs text-steel">Default item for invoice lines</label>
              <Select
                className="px-2 py-1.5"
                value={profile.defaultItemId}
                onChange={(e) => setProfile({ ...profile, defaultItemId: e.target.value })}
              >
                <option value="">Auto ("Clevis Services")</option>
                {items.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                    {i.type ? ` (${i.type})` : ""}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="mb-0.5 block text-xs text-steel">Class (optional)</label>
              <Select
                className="px-2 py-1.5"
                value={profile.classId}
                onChange={(e) => setProfile({ ...profile, classId: e.target.value })}
              >
                <option value="">— none —</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-ink">
            <input
              type="checkbox"
              checked={profile.itemStrategy === "matchByName"}
              onChange={(e) =>
                setProfile({ ...profile, itemStrategy: e.target.checked ? "matchByName" : "generic" })
              }
            />
            When a line's description exactly matches one of my QuickBooks items, use that item
          </label>
          <Button className="px-3 py-1 text-xs" onClick={() => void save()} busy={busy === "save"}>
            Save sync settings
          </Button>
        </div>
      )}
      {message ? <p className="mt-1 text-xs text-ink">{message}</p> : null}
    </div>
  );
}

interface QbdCredentials {
  username: string;
  password: string;
  qwcXml: string;
}

// The Web Connector connect wizard, ported from skuru: one click generates
// everything; the page then walks the user through the few QBWC steps and
// turns green on the first check-in.
function QbdSection() {
  const { tenantId } = useAuth();
  const [lastSync, setLastSync] = useState<{ at: number; ok: boolean; message: string } | null>(null);
  const [creds, setCreds] = useState<QbdCredentials | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [scanNote, setScanNote] = useState("");
  const [copied, setCopied] = useState(false);
  const [, forceTick] = useState(0);

  useEffect(() => {
    if (!tenantId) return;
    const unsub = onSnapshot(doc(db, paths.tenantIntegrationConfig(tenantId, "quickbooks-desktop")), (snap) =>
      setLastSync((snap.get("lastSync") as { at: number; ok: boolean; message: string }) ?? null),
    );
    const t = setInterval(() => forceTick((v) => v + 1), 30000);
    return () => {
      unsub();
      clearInterval(t);
    };
  }, [tenantId]);

  async function setup() {
    if (!tenantId) return;
    setBusy(true);
    setError("");
    try {
      const res = await call<QbdCredentials>("startQbDesktopConnect", { tenantId }, { mutation: false });
      setCreds(res);
      setCopied(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function downloadQwc() {
    if (!creds) return;
    const blob = new Blob([creds.qwcXml], { type: "application/xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "clevis.qwc";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const minsAgo = lastSync ? Math.round((Date.now() - lastSync.at) / 60000) : null;
  const polling = lastSync?.ok === true && minsAgo !== null && minsAgo < 10;

  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardTitle className="mb-0">QuickBooks Desktop</CardTitle>
        {lastSync ? (
          <span
            className={`rounded-full px-2 py-0.5 text-xs ${polling ? "bg-success/15 text-success" : "bg-steel/15 text-steel"}`}
          >
            {polling
              ? `Connected · checked in ${minsAgo === 0 ? "just now" : `${minsAgo}m ago`}`
              : lastSync.message}
          </span>
        ) : null}
      </div>

      {!creds ? (
        <>
          <p className="my-3 text-sm text-steel">
            One click sets everything up — Clevis generates your connection file and password, then
            walks you through the three steps in the Web Connector.
          </p>
          {lastSync ? (
            <ConfirmButton
              variant="primary"
              confirm="Regenerate? The old password and .qwc stop working."
              confirmLabel="Regenerate"
              onConfirm={() => void setup()}
              busy={busy}
            >
              Regenerate connection
            </ConfirmButton>
          ) : (
            <Button onClick={() => void setup()} busy={busy}>
              Set up QuickBooks Desktop
            </Button>
          )}
        </>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="rounded-lg border border-brandorange/40 bg-brandorange/5 p-3">
            <p className="mb-1 text-xs font-semibold text-brandorange-dark">
              Your Web Connector password — shown only once:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded bg-white px-2 py-1 font-mono text-sm text-ink">{creds.password}</code>
              <Button
                variant="secondary"
                className="px-2 py-1 text-xs"
                onClick={() => {
                  void navigator.clipboard.writeText(creds.password);
                  setCopied(true);
                }}
              >
                {copied ? "Copied ✓" : "Copy"}
              </Button>
            </div>
          </div>
          <ol className="list-decimal space-y-1.5 pl-5 text-sm text-ink">
            <li>
              <Button variant="secondary" className="px-2 py-0.5 text-xs" onClick={downloadQwc}>
                Download clevis.qwc
              </Button>
            </li>
            <li>
              Open <strong>QuickBooks Desktop</strong> with your company file (run as administrator the
              first time).
            </li>
            <li>
              Double-click the downloaded <strong>clevis.qwc</strong>. QuickBooks asks to authorize —
              choose <em>"Yes, always allow access even if QuickBooks is not running"</em> and press OK.
            </li>
            <li>
              In the Web Connector window, click the <strong>Password</strong> box next to Clevis, paste
              the password, press Tab, and answer <em>Yes</em> to save it.
            </li>
            <li>
              Tick the checkbox next to <strong>Clevis</strong> and press <strong>Update Selected</strong>.
            </li>
          </ol>
          <p className="text-xs text-steel">
            The badge above turns green at the first check-in — from then on it runs automatically
            every minute. (No Web Connector installed? It ships with QuickBooks; otherwise download it
            from Intuit's site.)
          </p>
        </div>
      )}
      {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
      {scanNote ? <p className="mt-2 text-xs text-steel">{scanNote}</p> : null}
      {lastSync && !creds ? <QbLearnCard target="desktop" /> : null}
      {lastSync && !creds ? <BillsCard target="desktop" /> : null}
      {lastSync && !creds ? (
        <Button
          type="button"
          variant="ghost"
          className="mr-2 mt-3 px-3 py-1 text-xs"
          busy={busy}
          onClick={async () => {
            if (!tenantId) return;
            setBusy(true);
            setScanNote("");
            try {
              const res = await call<{ outcome: string }>("refreshQbdCustomerCache", { tenantId });
              setScanNote(
                res.outcome === "queued"
                  ? "Customer scan queued — it runs on the Web Connector's next check-in (about a minute), then name matching uses the fresh list."
                  : res.outcome === "already_queued"
                    ? "A customer scan is already waiting for the Web Connector."
                    : "Connect QuickBooks Desktop first.",
              );
            } catch (err) {
              setError((err as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          Refresh customer list
        </Button>
      ) : null}
      {lastSync && !creds ? (
        <ConfirmButton
          className="mt-3 px-3 py-1 text-xs"
          confirm="Disconnect QuickBooks Desktop?"
          confirmLabel="Disconnect"
          onConfirm={async () => {
            if (!tenantId) return;
            await call("disconnectQbDesktop", { tenantId }, { mutation: false }).catch((err) =>
              setError((err as Error).message),
            );
          }}
        >
          Disconnect
        </ConfirmButton>
      ) : null}
    </Card>
  );
}

// The Bills settings card, when the owner gave this person the Bills sub-section.
function BillsCard({ target }: { target: "online" | "desktop" }) {
  const can = useAccess();
  if (!can("quickbooks.bills")) return null;
  return (
    <div id="bills">
      <QbBillSettingsCard target={target} />
    </div>
  );
}
